import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { AppTypeDescriptor } from '@internal/plugin-waadoo-config-common';
import { appLogoKeys, useAppLogoKeys, type AppLogoKeys } from './appIcons';
import { useAppTitle } from './appTitle';
import {
  fallbackDescriptor,
  useAppTypeDescriptor,
} from './useAppTypeDescriptor';

/**
 * The `AppType` of the project a view is about, and its compiled descriptor
 * (`useAppTypeDescriptor`), for the components deep in it that name its
 * application (« Ouvrir Odoo », « Odoo prêt »…) or what its chart deploys
 * (pod names, addresses), or show its logo (`useAppLogoKeys`), without
 * being given the project: set by the
 * branch page, the environments card, the promotion card and the home row's
 * menu. Without a provider — a component rendered alone —, unknown: the
 * legacy name (`useAppTitle`), logo (`appLogoKeys`) and the embedded Odoo
 * descriptor.
 */
interface CurrentAppType {
  appType: string | undefined;
  descriptor: AppTypeDescriptor | undefined;
  logo: AppLogoKeys;
}

const AppTypeContext = createContext<CurrentAppType | undefined>(undefined);

export function AppTypeProvider(props: {
  appType: string | undefined;
  children: ReactNode;
}) {
  const { appType } = props;
  const descriptor = useAppTypeDescriptor(appType);
  const logo = useAppLogoKeys(appType);
  const value = useMemo(
    () => ({ appType, descriptor, logo }),
    [appType, descriptor, logo],
  );
  return (
    <AppTypeContext.Provider value={value}>
      {props.children}
    </AppTypeContext.Provider>
  );
}

/** The `AppType` of the view's project; undefined without a provider. */
export function useCurrentAppType(): string | undefined {
  return useContext(AppTypeContext)?.appType;
}

/**
 * The compiled descriptor of the view's project (`useAppTypeDescriptor`);
 * without a provider, the embedded Odoo descriptor (`fallbackDescriptor`).
 */
export function useCurrentAppDescriptor(): AppTypeDescriptor | undefined {
  const current = useContext(AppTypeContext);
  return current ? current.descriptor : fallbackDescriptor(undefined);
}

const NO_PROVIDER_LOGO = appLogoKeys(undefined);

/**
 * The icon keys of the view's project's application (`useAppLogoKeys`);
 * without a provider, the legacy type's (`appLogoKeys`).
 */
export function useCurrentAppLogo(): AppLogoKeys {
  return useContext(AppTypeContext)?.logo ?? NO_PROVIDER_LOGO;
}

/** The name of the view's project's application (`useAppTitle`). */
export function useCurrentAppTitle(): string {
  return useAppTitle(useCurrentAppType());
}
